import { StandardEvents } from '@shopify/events';

class RiftVariantOptionMediaGroup extends HTMLElement {
  #abortController = new AbortController();
  #openOptionValueId = null;
  #positionFrame = 0;
  #touchInteraction = window.matchMedia('(max-width: 749px), (hover: none), (pointer: coarse)');
  #variantPicker;

  connectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#variantPicker = this.closest('variant-picker');

    this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal: this.#abortController.signal });
    this.addEventListener('pointerover', this.#handlePointerOver, { signal: this.#abortController.signal });
    this.addEventListener('pointerout', this.#handlePointerOut, { signal: this.#abortController.signal });
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, {
      capture: true,
      signal: this.#abortController.signal,
    });
    document.addEventListener('keydown', this.#handleKeydown, { signal: this.#abortController.signal });
    window.addEventListener('resize', this.#handleViewportChange, {
      passive: true,
      signal: this.#abortController.signal,
    });
    this.#variantPicker?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    window.cancelAnimationFrame(this.#positionFrame);
  }

  #handleClick = (event) => {
    const label = this.#getOptionLabel(event.target);
    if (!label) return;

    label.removeAttribute('data-rift-tooltip-suppressed');
    this.#positionTooltip(label);
    if (!this.#touchInteraction.matches) return;

    const input = label.querySelector('input[data-option-value-id]');
    this.#openOptionValueId = input?.dataset.optionValueId || null;
    this.#renderTouchState();
  };

  #handleFocusIn = (event) => {
    const label = this.#getOptionLabel(event.target);
    label?.removeAttribute('data-rift-tooltip-suppressed');
    if (label) this.#positionTooltip(label);
  };

  #handlePointerOver = (event) => {
    const label = this.#getOptionLabel(event.target);
    if (label) this.#positionTooltip(label);
  };

  #handlePointerOut = (event) => {
    const label = this.#getOptionLabel(event.target);
    if (!label || (event.relatedTarget instanceof Node && label.contains(event.relatedTarget))) return;
    label.removeAttribute('data-rift-tooltip-suppressed');
  };

  #handleDocumentPointerDown = (event) => {
    if (!this.#touchInteraction.matches) return;

    const label = this.#getOptionLabel(event.target);
    if (label) return;

    this.#openOptionValueId = null;
    this.#renderTouchState();
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    this.#openOptionValueId = null;
    this.#renderTouchState();

    const visibleLabel = this.querySelector(
      '[data-rift-tooltip-open], .rift-variant-option-media__label:focus-within, .rift-variant-option-media__label:hover'
    );
    visibleLabel?.setAttribute('data-rift-tooltip-suppressed', 'true');
  };

  #handleViewportChange = () => {
    this.#renderTouchState();
    this.#queueVisibleTooltipPositions();
  };

  #handleProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (!this.isConnected) return;

        if (detail?.newProduct) {
          this.#openOptionValueId = null;
        }

        this.#renderTouchState();
        this.#queueVisibleTooltipPositions();
      })
      .catch(() => {});
  };

  #renderTouchState() {
    let openLabel = null;

    for (const label of this.querySelectorAll('[data-rift-variant-media-option]')) {
      const input = label.querySelector('input[data-option-value-id]');
      const isOpen = Boolean(
        this.#touchInteraction.matches &&
          this.#openOptionValueId &&
          input?.dataset.optionValueId === this.#openOptionValueId
      );

      label.toggleAttribute('data-rift-tooltip-open', isOpen);
      if (isOpen) {
        label.removeAttribute('data-rift-tooltip-suppressed');
        openLabel = label;
      }
    }

    if (openLabel) window.requestAnimationFrame(() => this.#positionTooltip(openLabel));
  }

  #queueVisibleTooltipPositions() {
    window.cancelAnimationFrame(this.#positionFrame);
    this.#positionFrame = window.requestAnimationFrame(() => {
      if (!this.isConnected) return;

      for (const label of this.querySelectorAll(
        '[data-rift-tooltip-open], .rift-variant-option-media__label:focus-within, .rift-variant-option-media__label:hover'
      )) {
        this.#positionTooltip(label);
      }
    });
  }

  #positionTooltip(label) {
    const tooltip = label.querySelector('.rift-variant-option-media__tooltip');
    if (!(tooltip instanceof HTMLElement)) return;

    tooltip.style.setProperty('--rift-variant-tooltip-shift-inline', '0px');
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportMargin = 12;
    let shift = 0;

    if (tooltipRect.left < viewportMargin) {
      shift = viewportMargin - tooltipRect.left;
    } else if (tooltipRect.right > window.innerWidth - viewportMargin) {
      shift = window.innerWidth - viewportMargin - tooltipRect.right;
    }

    tooltip.style.setProperty('--rift-variant-tooltip-shift-inline', `${Math.round(shift)}px`);
  }

  #getOptionLabel(target) {
    if (!(target instanceof Element)) return null;

    const label = target.closest('[data-rift-variant-media-option]');
    return label instanceof HTMLElement && this.contains(label) ? label : null;
  }
}

if (!customElements.get('rift-variant-option-media-group')) {
  customElements.define('rift-variant-option-media-group', RiftVariantOptionMediaGroup);
}
